import { useState } from "react";
import {
  Container,
  Title,
  Text,
  TextInput,
  Button,
  Paper,
  Stack,
  Alert,
  PinInput,
  Group,
  Transition,
  ThemeIcon,
  Box,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconCheck,
  IconShieldLock,
  IconMail,
  IconTrash,
} from "@tabler/icons-react";
import { PerformRequest } from "../../hooks/usePerformRequest";
import { Endpoints } from "../../lib/endpoints";
import "./styles.scss";

export default function DeleteAccount() {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState<"request" | "verify" | "success">("request");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRequestOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    try {
      const { data, status, message } = await PerformRequest<any>({
        ...Endpoints.RequestAccountDeletion(),
        data: { email },
      });

      if (status !== 200 || !data?.success) {
        throw new Error(
          message || data?.message || "Failed to request deletion.",
        );
      }

      setStep("verify");
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (otp.length !== 4) {
      setError("Please enter the 4-digit verification code.");
      return;
    }

    setLoading(true);
    try {
      const { data, status, message } = await PerformRequest<any>({
        ...Endpoints.VerifyAccountDeletion(),
        data: { email, otp },
      });

      if (status !== 200 || !data?.success) {
        throw new Error(
          message || data?.message || "Failed to verify deletion request.",
        );
      }

      setStep("success");
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="delete-account-page">
      <div className="policy-orbs">
        <div className="orb orb-1"></div>
        <div className="orb orb-2"></div>
        <div className="orb orb-3"></div>
      </div>

      <Container size="sm" className="delete-container">
        <Paper radius="xl" p="xl" className="delete-card">
          <Transition
            mounted={step === "request"}
            transition="fade"
            duration={400}
            timingFunction="ease"
          >
            {(styles) => (
              <Box
                style={{
                  ...styles,
                  display: step === "request" ? "block" : "none",
                }}
              >
                <ThemeIcon
                  size={64}
                  radius="xl"
                  variant="light"
                  color="red"
                  className="icon-header"
                >
                  <IconTrash size={32} />
                </ThemeIcon>
                <Title order={2} className="title">
                  Delete Account
                </Title>
                <Text c="dimmed" size="sm" className="subtitle" mb="xl">
                  Requesting account deletion will permanently remove your
                  profile, data, and access to the platform. This action cannot
                  be undone. Enter your email to receive a verification code.
                </Text>

                <form onSubmit={handleRequestOTP}>
                  <Stack>
                    {error && (
                      <Alert
                        icon={<IconAlertCircle size={16} />}
                        title="Error"
                        color="red"
                        variant="filled"
                        radius="md"
                      >
                        {error}
                      </Alert>
                    )}
                    <TextInput
                      required
                      name="email"
                      label="Email Address"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.currentTarget.value)}
                      radius="md"
                      size="md"
                      leftSection={<IconMail size={16} stroke={1.5} />}
                      classNames={{ input: "premium-input" }}
                    />
                    <Button
                      fullWidth
                      size="md"
                      radius="md"
                      type="submit"
                      loading={loading}
                      color="red"
                      className="submit-button"
                    >
                      Send Verification Code
                    </Button>
                  </Stack>
                </form>
              </Box>
            )}
          </Transition>

          <Transition
            mounted={step === "verify"}
            transition="fade"
            duration={400}
            timingFunction="ease"
          >
            {(styles) => (
              <Box
                style={{
                  ...styles,
                  display: step === "verify" ? "block" : "none",
                }}
              >
                <ThemeIcon
                  size={64}
                  radius="xl"
                  variant="light"
                  color="yellow"
                  className="icon-header"
                >
                  <IconShieldLock size={32} />
                </ThemeIcon>
                <Title order={2} className="title">
                  Verify Identity
                </Title>
                <Text c="dimmed" size="sm" className="subtitle" mb="xl">
                  We've sent a 4-digit code to <strong>{email}</strong>. Please
                  enter it below to confirm your deletion request.
                </Text>

                <form onSubmit={handleVerifyOTP}>
                  <Stack>
                    {error && (
                      <Alert
                        icon={<IconAlertCircle size={16} />}
                        title="Error"
                        color="red"
                        variant="filled"
                        radius="md"
                      >
                        {error}
                      </Alert>
                    )}

                    <Group justify="center" mb="md">
                      <PinInput
                        length={4}
                        value={otp}
                        onChange={setOtp}
                        placeholder="○"
                        size="xl"
                        radius="md"
                        type="number"
                      />
                    </Group>

                    <Button
                      fullWidth
                      size="md"
                      radius="md"
                      type="submit"
                      loading={loading}
                      color="red"
                      className="submit-button"
                    >
                      Confirm Deletion
                    </Button>
                    <Button
                      variant="subtle"
                      size="sm"
                      onClick={() => setStep("request")}
                      disabled={loading}
                      color="gray"
                    >
                      Use a different email
                    </Button>
                  </Stack>
                </form>
              </Box>
            )}
          </Transition>

          <Transition
            mounted={step === "success"}
            transition="fade"
            duration={400}
            timingFunction="ease"
          >
            {(styles) => (
              <Box
                style={{
                  ...styles,
                  display: step === "success" ? "block" : "none",
                }}
                className="success-step"
              >
                <ThemeIcon
                  size={80}
                  radius="xl"
                  variant="filled"
                  color="green"
                  className="icon-success"
                  mb="xl"
                >
                  <IconCheck size={40} stroke={3} />
                </ThemeIcon>
                <Title order={2} className="title">
                  Request Submitted
                </Title>
                <Text c="dimmed" size="md" className="subtitle" mt="md" mb="xl">
                  Your account deletion request has been successfully submitted
                  to our administrators. We will process your request and
                  permanently delete your data within 7-14 business days.
                </Text>

                <Button
                  component="a"
                  href="/"
                  size="md"
                  radius="md"
                  variant="light"
                  fullWidth
                  className="return-button"
                >
                  Return to Home
                </Button>
              </Box>
            )}
          </Transition>
        </Paper>
      </Container>
    </div>
  );
}
